import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { AlertTriangle, UserX, TrendingUp } from 'lucide-react';

// --------- HELPERS ----------
const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

function computeSafeTooltipPosition({ barRect, containerRect, tipW, tipH, prefAbove = true }) {
  const PADDING = 8;
  const OFFSET  = 10;

  // centro da barra relativo ao container
  let x = (barRect.left - containerRect.left) + (barRect.width / 2);

  const yTop    = (barRect.top - containerRect.top) - OFFSET - tipH; // acima
  const yBottom = (barRect.bottom - containerRect.top) + OFFSET;      // abaixo

  let placeAbove = prefAbove;
  let y = placeAbove ? yTop : yBottom;

  // se acima não cabe, tenta abaixo
  if (placeAbove && y < PADDING) {
    placeAbove = false;
    y = yBottom;
  }

  const containerH = containerRect.height;
  // se abaixo não cabe, volta acima
  if (!placeAbove && (y + tipH + PADDING > containerH)) {
    placeAbove = true;
    y = yTop;
  }

  // clamp final
  y = clamp(y, PADDING, Math.max(PADDING, containerH - tipH - PADDING));

  // como usamos translateX(-50%), clamp considerando meia-largura
  const minCenter = PADDING + tipW / 2;
  const maxCenter = containerRect.width - PADDING - tipW / 2;
  x = clamp(x, minCenter, maxCenter);

  return { x, y, placeAbove };
}
// ----------------------------

export default function AccidentChart() {
  const [animatedData, setAnimatedData] = useState([]);
  const [hoveredBarKey, setHoveredBarKey] = useState(null);   // `${index}-accidents` | `${index}-fatalities`
  const [tooltipData, setTooltipData] = useState(null);        // { year, value, label }
  const [tooltipPos,  setTooltipPos]  = useState(null);        // { x, y, placeAbove }
  const [isMobile,    setIsMobile]    = useState(false);

  // Refs para posicionamento do tooltip
  const scrollRef       = useRef(null);
  const innerRefDesktop = useRef(null);
  const innerRefMobile  = useRef(null);
  const tooltipRef      = useRef(null);

  const accidentData = [
    { year: 2010, accidents: 183.5, fatalities: 8.6 }, { year: 2011, accidents: 192.3, fatalities: 8.7 },
    { year: 2012, accidents: 184.6, fatalities: 8.7 }, { year: 2013, accidents: 186.7, fatalities: 8.4 },
    { year: 2014, accidents: 169.2, fatalities: 8.2 }, { year: 2015, accidents: 122.2, fatalities: 6.9 },
    { year: 2016, accidents: 96.4, fatalities: 6.4 },  { year: 2017, accidents: 89.6, fatalities: 6.2 },
    { year: 2018, accidents: 69.3, fatalities: 5.3 },  { year: 2019, accidents: 67.4, fatalities: 5.3 },
    { year: 2020, accidents: 63.5, fatalities: 5.3 },  { year: 2021, accidents: 64.5, fatalities: 5.4 },
    { year: 2022, accidents: 64.4, fatalities: 5.4 },  { year: 2023, accidents: 67.6, fatalities: 5.6 },
    { year: 2024, accidents: 73.2, fatalities: 6.2 }
  ];

  const SCALE_FATALITIES = 10; // x10 para visualizar junto
  const CHART_HEIGHT = 320;
  const maxValue = Math.max(...accidentData.map(d => Math.max(d.accidents, d.fatalities * SCALE_FATALITIES)));

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setAnimatedData(accidentData), 400);
    return () => clearTimeout(t);
  }, []);

  const getBarHeight = (v) => (v / maxValue) * (CHART_HEIGHT - 40);

  const clsAcc = (active) => `rounded-t cursor-pointer transition-all duration-300 ${active ? 'bg-red-600'    : 'bg-red-500 hover:bg-red-600'}`;
  const clsFat = (active) => `rounded-t cursor-pointer transition-all duration-300 ${active ? 'bg-orange-600' : 'bg-orange-500 hover:bg-orange-600'}`;

  // Tooltip com posicionamento seguro (duplo RAF para iOS/Safari)
  const showTooltip = (item, key, targetEl) => {
    const container = (isMobile ? innerRefMobile : innerRefDesktop).current;
    if (!container) return;

    const barRect       = targetEl.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    setHoveredBarKey(key);

    const isAcc = key.endsWith('accidents');
    const raw   = isAcc ? item.accidents : item.fatalities;
    const label = isAcc ? 'Sinistros' : `Vítimas Fatais (×${SCALE_FATALITIES})`;

    // 1) Renderiza tooltip
    setTooltipData({ year: item.year, value: raw, label });

    // 2) Mede no(s) próximo(s) frame(s)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const tip = tooltipRef.current;
        const tipW = tip ? tip.offsetWidth  : 140;
        const tipH = tip ? tip.offsetHeight : 44;

        const { x, y, placeAbove } = computeSafeTooltipPosition({
          barRect,
          containerRect,
          tipW,
          tipH,
          prefAbove: !isMobile // mobile: abaixo; desktop: acima
        });
        setTooltipPos({ x, y, placeAbove });
      });
    });
  };

  const handleEnter = (item, key) => (e) => showTooltip(item, key, e.currentTarget);
  const handleLeave = () => {
    setHoveredBarKey(null);
    setTooltipData(null);
    setTooltipPos(null);
  };

  // Handlers unificados (Pointer Events)
  const getBarHandlers = (item, key) => ({
    onPointerDown:  (e) => showTooltip(item, key, e.currentTarget),
    onPointerEnter: (e) => !isMobile && showTooltip(item, key, e.currentTarget),
    onPointerMove:  (e) => !isMobile && showTooltip(item, key, e.currentTarget),
    onPointerLeave: handleLeave,
  });

  // --- MOBILE layout ---
  const BAR_W_MOBILE = 16;
  const GAP_BETWEEN_BARS   = 6;
  const GAP_BETWEEN_GROUPS = 12;
  const GROUP_W_MOBILE = (BAR_W_MOBILE * 2) + GAP_BETWEEN_BARS + GAP_BETWEEN_GROUPS;
  const contentWidthMobile = (accidentData.length * GROUP_W_MOBILE) + 16;
  const touchSafeStyle = { touchAction: 'manipulation', userSelect: 'none' };

  return (
    <div className="space-y-8">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-center">Sinistros em Rodovias Federais</CardTitle>
          <p className="text-center text-gray-600">Ocorrências e vítimas fatais no Brasil (em milhares)</p>
        </CardHeader>
        <CardContent>
          {isMobile ? (
            // --- MOBILE ---
            <div className="overflow-x-auto no-scrollbar" ref={scrollRef} onScroll={handleLeave}>
              <div
                className="relative flex items-end px-4"
                ref={innerRefMobile}
                style={{ height: CHART_HEIGHT, width: contentWidthMobile, paddingTop: 6 }}
              >
                {accidentData.map((item, index) => (
                  <div key={item.year} className="flex flex-col items-center justify-end" style={{ width: GROUP_W_MOBILE }}>
                    <div className="flex items-end" style={{ gap: GAP_BETWEEN_BARS }}>
                      {/* Sinistros */}
                      <div
                        className={clsAcc(hoveredBarKey === `${index}-accidents`)}
                        style={{
                          ...touchSafeStyle,
                          width: BAR_W_MOBILE,
                          height: `${animatedData[index] ? getBarHeight(item.accidents) : 0}px`,
                          transitionDelay: `${index * 40}ms`,
                          transform: hoveredBarKey === `${index}-accidents` ? 'translateY(-2px)' : 'translateY(0)'
                        }}
                        {...getBarHandlers(item, `${index}-accidents`)}
                      />
                      {/* Vítimas fatais (escala x10) */}
                      <div
                        className={clsFat(hoveredBarKey === `${index}-fatalities`)}
                        style={{
                          ...touchSafeStyle,
                          width: BAR_W_MOBILE,
                          height: `${animatedData[index] ? getBarHeight(item.fatalities * SCALE_FATALITIES) : 0}px`,
                          transitionDelay: `${index * 40 + 16}ms`,
                          transform: hoveredBarKey === `${index}-fatalities` ? 'translateY(-2px)' : 'translateY(0)'
                        }}
                        {...getBarHandlers(item, `${index}-fatalities`)}
                      />
                    </div>
                    <span className="text-[10px] mt-1 text-gray-500">{String(item.year).slice(2)}</span>
                  </div>
                ))}

                {tooltipData && (
                  <div
                    ref={tooltipRef}
                    className="absolute z-10 bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg whitespace-nowrap pointer-events-none"
                    style={{
                      left: tooltipPos ? tooltipPos.x : -9999,
                      top: tooltipPos ? tooltipPos.y : -9999,
                      transform: tooltipPos?.placeAbove ? 'translate(-50%,-10%)' : 'translate(-50%,10%)'
                    }}
                  >
                    <div className="text-sm font-semibold">{tooltipData.year}</div>
                    <div className="text-xs">
                      {tooltipData.label}: {tooltipData.value.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} mil
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            // --- DESKTOP ---
            <div className="h-80 px-4 relative" ref={innerRefDesktop}>
              <div className="flex items-end justify-between h-full gap-2">
                {accidentData.map((item, index) => (
                  <div key={item.year} className="flex flex-col items-center justify-end h-full">
                    <div className="flex items-end gap-1 h-full">
                      {/* Sinistros */}
                      <div
                        className={clsAcc(hoveredBarKey === `${index}-accidents`) + " w-4"}
                        style={{
                          ...touchSafeStyle,
                          height: `${animatedData[index] ? getBarHeight(item.accidents) : 0}px`,
                          transitionDelay: `${index * 50}ms`,
                          transform: hoveredBarKey === `${index}-accidents` ? 'translateY(-2px)' : 'translateY(0)'
                        }}
                        {...getBarHandlers(item, `${index}-accidents`)}
                      />
                      {/* Vítimas fatais (escala x10) */}
                      <div
                        className={clsFat(hoveredBarKey === `${index}-fatalities`) + " w-4"}
                        style={{
                          ...touchSafeStyle,
                          height: `${animatedData[index] ? getBarHeight(item.fatalities * SCALE_FATALITIES) : 0}px`,
                          transitionDelay: `${index * 50 + 20}ms`,
                          transform: hoveredBarKey === `${index}-fatalities` ? 'translateY(-2px)' : 'translateY(0)'
                        }}
                        {...getBarHandlers(item, `${index}-fatalities`)}
                      />
                    </div>
                    <span className="text-xs mt-2 text-gray-500">{item.year}</span>
                  </div>
                ))}
              </div>

              {tooltipData && (
                <div
                  ref={tooltipRef}
                  className="absolute z-10 bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg whitespace-nowrap pointer-events-none"
                  style={{
                    left: tooltipPos ? tooltipPos.x : -9999,
                    top: tooltipPos ? tooltipPos.y : -9999,
                    transform: tooltipPos?.placeAbove ? 'translate(-50%,-10%)' : 'translate(-50%,10%)'
                  }}
                >
                  <div className="text-sm font-semibold">{tooltipData.year}</div>
                  <div className="text-xs">
                    {tooltipData.label}: {tooltipData.value.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} mil
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Legenda */}
          <div className="mt-6 flex justify-center gap-6">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-red-500 rounded" />
              <AlertTriangle className="w-4 h-4 mr-1" />
              <span>Sinistros</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-orange-500 rounded" />
              <UserX className="w-4 h-4 mr-1" />
              <span>Vítimas Fatais (escala ×{SCALE_FATALITIES})</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cards analíticos */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card className="bg-gradient-to-br from-red-50 to-orange-100">
          <CardHeader>
            <CardTitle className="text-xl font-bold text-red-900 flex items-center gap-2">
              <TrendingUp className="w-6 h-6" />
              Aumento de Ocorrências em 2024
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-white/60 p-4 rounded-lg">
              <h4 className="font-semibold text-red-800 mb-2">Total de Sinistros</h4>
              <p className="text-sm text-gray-700">
                Em 2024, foram registradas <strong>73,2 mil ocorrências</strong>, um aumento de <strong>8,2%</strong> em relação a 2023 — maior nível desde 2014.
              </p>
            </div>
            <div className="bg-white/60 p-4 rounded-lg">
              <h4 className="font-semibold text-red-800 mb-2">Análise Histórica Recente</h4>
              <p className="text-sm text-gray-700">
                Desde 2018 há tendência de alta em sinistros e vítimas, demandando foco em fiscalização,
                infraestrutura segura e metas do PNATRANS.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-50 to-amber-100">
          <CardHeader>
            <CardTitle className="text-xl font-bold text-orange-900 flex items-center gap-2">
              <UserX className="w-6 h-6" />
              Aumento no Número de Vítimas Fatais
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-white/60 p-4 rounded-lg">
              <h4 className="font-semibold text-orange-800 mb-2">Total de Vítimas Fatais</h4>
              <p className="text-sm text-gray-700">
                2024 registrou <strong>6,2 mil mortes</strong> (+<strong>9,6%</strong> vs 2023), reforçando a urgência
                de intervenções estruturais e educação no trânsito.
              </p>
            </div>
            <div className="bg-white/60 p-4 rounded-lg">
              <h4 className="font-semibold text-orange-800 mb-2">Variação Acumulada</h4>
              <p className="text-sm text-gray-700">
                Apesar dos aumentos recentes, a queda acumulada desde 2010 segue relevante, refletindo avanços em segurança.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
